/**
 * Disclosure search — one command palette across filings, press releases,
 * documents and the site's own pages. Opens with ⌘K / Ctrl+K.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { search } from '../lib/api';
import { matchPages } from '../data/searchIndex';
import { formatShortDate, categoryLabel } from '../lib/format';

const GROUPS = [
  { type: 'page', heading: 'Pages' },
  { type: 'announcement', heading: 'Announcements' },
  { type: 'press_release', heading: 'Press releases' },
  { type: 'document', heading: 'Documents' },
];

export default function SearchDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const needle = query.trim();

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setItems([]);
    }
  }, [open]);

  useEffect(() => {
    if (needle.length < 2) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      search(needle, { limit: 12 })
        .then((data) => {
          if (!cancelled) setItems([...matchPages(needle), ...(data.items || [])]);
        })
        .catch(() => {
          if (!cancelled) setItems(matchPages(needle));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [needle]);

  const go = useCallback(
    (item) => {
      onOpenChange(false);
      if (!item.url) return;
      if (/^https?:/i.test(item.url)) window.open(item.url, '_blank', 'noopener,noreferrer');
      else navigate(item.url);
    },
    [navigate, onOpenChange]
  );

  const seeAll = () => {
    if (needle.length < 2) return;
    onOpenChange(false);
    navigate(`/search?q=${encodeURIComponent(needle)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 search-dialog"
        style={{ maxWidth: 660 }}
        data-testid="search-dialog"
      >
        <DialogTitle className="u-sr-only">Search the site</DialogTitle>
        <DialogDescription className="u-sr-only">
          Search announcements released to SGXNet, press releases, documents and the pages of this
          site. Use the arrow keys to move between results.
        </DialogDescription>
        <Command shouldFilter={false} loop className="search-cmd">
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search announcements, releases and pages"
            data-testid="search-dialog-input"
          />
          <CommandList>
            {needle.length < 2 ? (
              <p className="search-cmd__hint" data-testid="search-dialog-hint">
                Type at least two characters. Every result is either a filing the Company released to
                SGXNet, a press release, or a page on this site.
              </p>
            ) : null}
            {needle.length >= 2 && loading && items.length === 0 ? (
              <p className="search-cmd__hint">Searching…</p>
            ) : null}
            {needle.length >= 2 && !loading && items.length === 0 ? (
              <p className="search-cmd__hint" data-testid="search-dialog-empty">
                Nothing matches “{needle}”.
              </p>
            ) : null}

            {GROUPS.map((group) => {
              const rows = items.filter((item) => item.type === group.type);
              if (rows.length === 0) return null;
              return (
                <CommandGroup key={group.type} heading={group.heading}>
                  {rows.map((item) => (
                    <CommandItem
                      key={`${item.type}-${item.url}-${item.title}`}
                      value={`${item.type}-${item.url}-${item.title}`}
                      onSelect={() => go(item)}
                      className="search-item"
                      data-testid="search-result-item"
                    >
                      <span className="search-item__title">{item.title}</span>
                      <span className="search-item__meta">
                        {item.date ? <span>{formatShortDate(item.date)}</span> : null}
                        {item.type === 'announcement' && item.category ? (
                          <span>{categoryLabel(item.category)}</span>
                        ) : null}
                        {item.type === 'press_release' ? <span>Press release</span> : null}
                        {item.type === 'page' ? <span>Page</span> : null}
                        {item.type === 'document' ? <span>Document</span> : null}
                      </span>
                      {item.snippet ? (
                        <span className="search-item__snippet">{item.snippet}</span>
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
          <div className="search-cmd__footer">
            <span className="search-cmd__keys" aria-hidden="true">
              ↑↓ move · ↵ open · esc close
            </span>
            <button
              type="button"
              className="search-cmd__all"
              onClick={seeAll}
              disabled={needle.length < 2}
              data-testid="search-see-all"
            >
              All results →
            </button>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
